import { useEffect, useRef, useState } from 'react';
import { useTheme, type ThemeMeta } from './theme';

export function ThemeSwitcher() {
  const { theme, setTheme, themes } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const current: ThemeMeta = themes.find((t) => t.id === theme) ?? themes[0]!;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="theme-switcher" ref={ref}>
      <button
        type="button"
        className="theme-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Tema: ${current.label}`}
      >
        <span
          className="theme-switcher-swatch"
          style={{
            background: `linear-gradient(135deg, ${current.swatch[0]} 50%, ${current.swatch[1]} 50%)`,
          }}
        />
        <span className="theme-switcher-label">{current.label}</span>
        <span className="theme-switcher-caret" aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <div className="theme-switcher-menu" role="listbox">
          {themes.map((t) => (
            <button
              key={t.id}
              type="button"
              role="option"
              aria-selected={t.id === theme}
              className={`theme-switcher-option ${t.id === theme ? 'is-active' : ''}`}
              onClick={() => {
                setTheme(t.id);
                setOpen(false);
              }}
              title={t.description}
            >
              <span
                className="theme-switcher-swatch"
                style={{
                  background: `linear-gradient(135deg, ${t.swatch[0]} 50%, ${t.swatch[1]} 50%)`,
                }}
              />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
